import { useState, useEffect } from 'react';
import { useFadeIn } from './useFadeIn';

const WORDS = [
  'Influencer Marketing.',
  'Artist Management.',
  'Social Media Strategy.',
  'Influencer PR.',
];

function useTypewriter(words, typeMs = 75, deleteMs = 45, pauseMs = 1900) {
  const [text, setText] = useState('');
  const [wIdx, setWIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const word = words[wIdx];
    let t;
    if (!deleting && text === word) {
      t = setTimeout(() => setDeleting(true), pauseMs);
    } else if (deleting && text === '') {
      setDeleting(false);
      setWIdx(i => (i + 1) % words.length);
    } else {
      const next = deleting
        ? word.slice(0, text.length - 1)
        : word.slice(0, text.length + 1);
      t = setTimeout(() => setText(next), deleting ? deleteMs : typeMs);
    }
    return () => clearTimeout(t);
  }, [text, deleting, wIdx, words, typeMs, deleteMs, pauseMs]);

  return text;
}

export default function Hero() {
  const typed = useTypewriter(WORDS);
  const ref = useFadeIn();

  return (
    <section id="about" data-testid="hero-section" className="hero">
      <div className="hero-bg">
        {/* Full-canvas ambient SVG */}
        <svg className="hero-bg-canvas" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
          {/* Dot grid */}
          {[...Array(18)].map((_, r) =>
            [...Array(30)].map((_, c) => (
              <circle key={`d-${r}-${c}`} cx={c * 52} cy={r * 52} r="1.2" fill="#000" opacity="0.045" />
            ))
          )}
          {/* Concentric rings — right anchor */}
          {[380, 290, 200, 115, 48].map((r, i) => (
            <circle key={`ring-${i}`} cx="1200" cy="450" r={r} stroke="#000" strokeWidth="0.8" fill="none" opacity="0.07" />
          ))}
          {/* Diagonal slash lines — bottom left energy */}
          {[0, 60, 120, 180, 240].map((offset, i) => (
            <line key={`sl-${i}`} x1={-60 + offset} y1="900" x2={340 + offset} y2="0"
              stroke="#000" strokeWidth="0.6" opacity="0.04" />
          ))}
          {/* Horizontal hairlines */}
          {[200, 360, 520, 680].map((y, i) => (
            <line key={`hl-${i}`} x1="0" y1={y} x2="520" y2={y}
              stroke="#000" strokeWidth="0.5" opacity="0.035" />
          ))}
          {/* Large TT monogram watermark */}
          <text x="680" y="600" fontFamily="serif" fontSize="420" fontWeight="700"
            fill="none" stroke="#000" strokeWidth="0.6" opacity="0.025"
            letterSpacing="-20">TT</text>
          {/* Crosshair accent — top right */}
          <line x1="1100" y1="80" x2="1100" y2="180" stroke="#000" strokeWidth="0.8" opacity="0.1"/>
          <line x1="1050" y1="130" x2="1150" y2="130" stroke="#000" strokeWidth="0.8" opacity="0.1"/>
          <circle cx="1100" cy="130" r="28" stroke="#000" strokeWidth="0.8" fill="none" opacity="0.07"/>
          {/* Small crosshair — bottom left */}
          <line x1="120" y1="760" x2="120" y2="840" stroke="#000" strokeWidth="0.8" opacity="0.08"/>
          <line x1="80" y1="800" x2="160" y2="800" stroke="#000" strokeWidth="0.8" opacity="0.08"/>
          <circle cx="120" cy="800" r="22" stroke="#000" strokeWidth="0.8" fill="none" opacity="0.06"/>
        </svg>
      </div>

      <div ref={ref} className="hero-inner fade-up in">
        <p className="hero-overline">Influencer Marketing Agency — India</p>

        <h1 data-testid="hero-headline" className="hero-headline">
          <span className="hero-headline-prefix">We do</span>
          <span className="hero-typewriter-line">
            <span data-testid="hero-typewriter-text" className="hero-typed">{typed}</span>
            <span className="hero-cursor" />
          </span>
        </h1>

        <p className="hero-sub">
        We move culture, not just metrics. Specializing in influencer marketing, content production, social media strategy, and influencer PR — we deliver end-to-end brand solutions that don't just get you noticed. They make you unforgettable.
        </p>

        <div data-testid="hero-cta" className="hero-ctas">
          <a href="#ourwork" className="btn-black">
            Our Work
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 7h12M7 1l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
          <a href="#contact" className="btn-outline">Get in Touch</a>
        </div>
      </div>

      <div className="scroll-indicator">
        <div className="scroll-indicator-line" />
        <div className="scroll-indicator-dot" />
      </div>
    </section>
  );
}
